import React from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowUp, faFolderOpen, faPen, faPlus, faTrash} from "@fortawesome/free-solid-svg-icons";
import EmptyState from "../../components/learner/EmptyState";

/**
 * Step 2 of the learning-path wizard — the ordered course list.
 *
 * Mirrors Stage 2 of the course builder: only reachable once the path shell is
 * saved, because courses attach to an existing learning path.
 */
const LearningPathCoursesStep = ({
  isNew,
  locked,
  status,
  pending,
  pathCourses,
  eligibleCourses,
  lessonCounts,
  moduleCounts,
  eligibleCoursesLoading,
  eligibleCoursesError,
  canSubmit,
  addCourseId,
  setAddCourseId,
  addPosition,
  setAddPosition,
  fieldClass,
  labelClass,
  primaryBtn,
  ghostBtn,
  onAddCourse,
  onMoveCourse,
  onRemoveCourse,
  onBuildCourse,
  onBackToDetails,
  onSubmit,
  onUnsubmit,
  onCreateCourse,
}) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="min-w-0">
        <h2 className="m-0 text-sm font-bold text-slate-900">
          2 · Course sequence ({pathCourses.length})
        </h2>
        <p className="m-0 mt-1 text-xs text-slate-500">
          Each course uses the course builder for its modules and lessons. Reorder courses to set the learning path.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={ghostBtn} onClick={onBackToDetails}>
          <FontAwesomeIcon icon={faArrowUp} /> Path details
        </button>
        {!isNew && status === "DRAFT" ? (
          <>
            <button type="button" className={ghostBtn} onClick={onCreateCourse}>
              <FontAwesomeIcon icon={faPlus} /> Create a course
            </button>
            <button type="button" className={primaryBtn} onClick={onSubmit} disabled={!canSubmit || pending}>
              <FontAwesomeIcon icon={faPen} /> Submit for review
            </button>
          </>
        ) : null}
        {!isNew && status === "PENDING" ? (
          <button type="button" className={ghostBtn} onClick={onUnsubmit}>
            Withdraw
          </button>
        ) : null}
      </div>
    </div>

    {isNew ? (
      <div className="mt-3">
        <EmptyState
          icon={faPen}
          title="Save the path first"
          hint="Create the draft on the Path details step."
        />
        <div className="mt-3 flex justify-center">
          <button type="button" className={primaryBtn} onClick={onBackToDetails}>
            Go to Path details
          </button>
        </div>
      </div>
    ) : (
      <>
        {pathCourses.length === 0 ? (
          <div className="mt-3">
            <EmptyState
              icon={faPlus}
              title="No courses yet"
              hint="Add at least one draft course with a lesson — submit requires it."
            />
          </div>
        ) : (
          <ol className="m-0 mt-3 grid list-none gap-2 p-0">
            {pathCourses.map((course, index) => (
              (() => {
                const courseId = course.courseId || course.course?.id || course.id;
                const title = course.title || course.courseTitle || course.course?.title || courseId;
                const moduleCount =
                  moduleCounts[courseId] ??
                  (Array.isArray(course.modules) ? course.modules.length : undefined);
                const lessonCount =
                  lessonCounts[courseId] ??
                  course.lessonCount ??
                  course.lessonsCount ??
                  (Array.isArray(course.lessons) ? course.lessons.length : undefined);
                return (
                  <li
                    key={courseId}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 p-3"
                  >
                    <div className="min-w-0">
                      <p className="m-0 truncate text-sm font-semibold text-slate-900">
                        {index + 1}. {title}
                      </p>
                      <p className="m-0 mt-1 text-xs text-slate-500">
                        {moduleCount === undefined ? "Modules" : `${moduleCount} module${moduleCount === 1 ? "" : "s"}`}
                        {" · "}
                        {lessonCount === undefined ? "Lessons" : `${lessonCount} lesson${lessonCount === 1 ? "" : "s"}`}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <button
                        type="button"
                        className={ghostBtn}
                        disabled={locked || pending}
                        onClick={() => onBuildCourse(course)}
                      >
                        <FontAwesomeIcon icon={faFolderOpen} /> Build course
                      </button>
                      <button
                        type="button"
                        className={ghostBtn}
                        aria-label={`Move ${title} up`}
                        disabled={locked || index === 0 || pending}
                        onClick={() => onMoveCourse(index, -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className={ghostBtn}
                        aria-label={`Move ${title} down`}
                        disabled={locked || index === pathCourses.length - 1 || pending}
                        onClick={() => onMoveCourse(index, 1)}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className={ghostBtn}
                        disabled={locked || pending}
                        onClick={() => onRemoveCourse(course)}
                      >
                        <FontAwesomeIcon icon={faTrash} /> Remove
                      </button>
                    </div>
                  </li>
                );
              })()
            ))}
          </ol>
        )}

        <form
          className="mt-4 grid gap-2 border-t border-slate-100 pt-4 sm:grid-cols-[1fr_110px_auto]"
          onSubmit={onAddCourse}
        >
          <div>
            <label className={labelClass}>Draft course with lessons</label>
            <select
              className={fieldClass}
              value={addCourseId}
              onChange={(e) => setAddCourseId(e.target.value)}
              disabled={locked || pending}
            >
              <option value="">Choose a draft course…</option>
              {eligibleCourses.map((course) => (
                <option key={course.id || course._id} value={course.id || course._id}>
                  {course.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Position</label>
            <input
              className={fieldClass}
              type="number"
              min="1"
              value={addPosition}
              onChange={(e) => setAddPosition(e.target.value)}
              placeholder="End"
              disabled={locked || pending}
            />
          </div>
          <div className="flex items-end">
            <button type="submit" className={primaryBtn} disabled={locked || pending}>
              <FontAwesomeIcon icon={faPlus} /> Add
            </button>
          </div>
        </form>

        {eligibleCoursesError ? (
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            Could not verify draft-course content: {eligibleCoursesError}
          </p>
        ) : null}
        {eligibleCourses.length === 0 ? (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="m-0 text-sm font-semibold text-amber-800">
              {eligibleCoursesLoading ? "Checking draft courses…" : "No eligible draft courses found."}
            </p>
            {!eligibleCoursesLoading ? (
              <p className="m-0 mt-1 text-xs text-amber-700">
                The path requires draft courses with at least one lesson. Create a course, add its modules
                and lessons in the course builder, then return here to add it to the sequence.
              </p>
            ) : null}
          </div>
        ) : null}
      </>
    )}
  </section>
);

export default LearningPathCoursesStep;
